<template>
  <div class="resume-editor-container">
    <ResumeEditor
      v-if="ready"
      ref="editor"
      class="resume-editor"
      :is-edit-mode="isEditMode"
      :isAddMode="isAddMode"
      :value="resumeInfo"
      :stepSetting="resumeEditorSetting"
      :isMiniResume="isMiniResume"
      :isBasicResume="isBasicResume"
      :isCandidate="true"
      :isNewPage="true"
      :saveBtnTextKey="saveBtnTextKey"
      :jobId="jobId"
      :companyId="companyId"
      :isFromPreference="isFromPreference"
      :cancelBtnTextI18nKey="cancelBtnTextI18nKey"
      @changeResumeSetting="handleChangeResumeSetting"
      @importResume="handelImportResume"
      @uploadResume="handleUploadResume"
      @cancel="handleCancel"
      @saveOpenQuestion="handelSaveOpenQuestion"
      @save="handleSave"
      @save-back="handleSaveAndBack"
      @rightBtnClick="handleRightBtnClick"
    />
    <DialogWrapper
      class="upload-success-dialog"
      :show.sync="uploadResumeSuccessDialogShow"
      :close-on-click-modal="false"
      :showClose="false"
      :hideFooter="true"
    >
      <div class="upload-success-container">
        <i class="result-icon iconfont iconcompleted"/>
        <span class="title-label">{{ $t(`${i18nPrefix}.label.uploadSuccess`) }}</span>
        <span class="title-tips">{{ $t(`${i18nPrefix}.tips.uploadSuccessTips`) }}</span>
        <el-button class="button to-personal-home" type="primary" @click="handleToHome(true)">{{ $t(`${i18nPrefix}.btn.toPersonalHome`) }}</el-button>
      </div>
    </DialogWrapper>

    <ResumeApplyDialog
      :resumeApplySuccessDialogShow="resumeApplySuccessDialogShow"
      :jobId="jobId"
      :assessment="assessment"
      :resumeApplyData="resumeApplyData"
      :loading="applying"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import ResumeEditor from '@/components/candidate/editor/layout.vue'
import { mixedRouterPush } from '@/router/routes'
import { STEP_TYPE } from '@/constant/candidate'
import { QUESTION_TYPE } from '@/constant/openQuestion'
import _ from 'lodash'
import DialogWrapper from '@/components/dialog/dialogWrapper.vue'
import ResumeApplyDialog from '@/components/candidate/editor/resumeApplyDialog.vue'
import { generateTempFileKey, getFileExtension, uploadFile } from '@/utils/oss'
import { AI_PARSE_MAX_COUNT } from '@/constant/application'

export default {
  components: {
    ResumeEditor,
    DialogWrapper,
    ResumeApplyDialog
  },
  data () {
    return {
      i18nPrefix: 'personal.resume',
      ready: false,
      readyPromise: [],
      hiddenIdNumber: true,
      resumeInfo: {},
      hiddenField: [],
      personalInfo: {},
      miniResumeHiddenStep: [STEP_TYPE.LANGUAGE, STEP_TYPE.ADDITIONAL_INFO],
      needUploadResume: false,
      resumeEditorSetting: {
        skipParseResume: false,
        onlyOpenQuestion: false,
        onlyInviteQuestion: false,
        showEdit: false,
        showRightBtn: false,
        profileStep: 0,
        hiddenStep: [STEP_TYPE.DESCRIPTION, STEP_TYPE.PRESCREEN_QUESTIONNAIRE, STEP_TYPE.JOB_QUESTIONNAIRE, STEP_TYPE.INVITE_QUESTIONNAIRE, STEP_TYPE.HR_QUESTIONNAIRE, STEP_TYPE.ONBOARDING_QUESTIONNAIRE],
        displayStep: [],
        [STEP_TYPE.UPLOAD_RESUME]: {
          showThirdParty: true,
          needUploadResume: false,
          onlyUploadResume: false,
          immediateUpload: false,
          showCreateNewBtn: false
        },
        [STEP_TYPE.BASE_INFO]: {
          hiddenField: ['onlyTalentPool', 'companyJob'],
          displayField: [],
          disabledField: ['mobile'],
          requiredField: ['emailAddress', 'mobile', 'gender', 'birthday', 'personalStatement', 'homeLocation', 'currentLocation'],
          loadJobData: false,
          isCandidate: true,
          showUploadButton: true,
          loadCandidateInfoWhenCreated: false
        },
        [STEP_TYPE.WORK_EXPERIENCE]: {
          interCount: 0,
          workCount: 0,
          isCandidate: true
        },
        [STEP_TYPE.EDUCATION]: {
          requiredCount: 1,
          isCandidate: true
        },
        [STEP_TYPE.LANGUAGE]: {
          requiredField: ['language', 'level', 'certification', 'score'],
          isCandidate: true
        },
        [STEP_TYPE.JOB_QUESTIONNAIRE]: {
          isCandidate: true
        }
      },
      uploadTask: {
        taskId: null,
        finished: false,
        percentage: 0
      },
      uploadResumeSuccessDialogShow: false,
      resumeApplySuccessDialogShow: false,
      resumeApplyData: {},
      useThirdPartyUpload: false,
      hasOpenQuestion: false,
      assessment: false,
      canReapply: false,
      finishedApply: false,
      applying: false
    }
  },
  computed: {
    ...mapGetters('company', ['companyThemeSetting']),
    isEditMode () {
      if (!this.isMiniResume && this.personalInfo?.profile_step !== 2) {
        return false
      }
      return this.$route.meta.isEditMode && !this.jobId
    },
    isAddMode () {
      return this.$route.meta.isAddMode && !this.jobId
    },
    isMiniResume () {
      return this.$route.meta.isMiniResume
    },
    isBasicResume () {
      return this.$route.meta.isBasicResume
    },
    applyUrl () {
      return this.$route.query.apply_url
    },
    jobId () {
      // TODO not use,  jobId in the path query is a bad param
      return this.$route.query.job_id || this.$route.query.jobId
    },
    companyId () {
      return this.$route.query.company_id
    },
    saveBtnTextKey () {
      return this.applyUrl ? 'common.btn.apply' : null
    },
    onlyUploadResume () {
      return this.$route.query.only_upload_resume === '1'
    },
    onlyOpenQuestion () {
      return this.$route.query.only_open_question === '1'
    },
    onlyInviteQuestion () {
      return this.$route.query.only_invite_question === '1'
    },
    onlyEditResume () {
      return this.$route.query.only_edit_resume === '1'
    },
    parseResume () {
      return this.$route.query.parse_resume === '1'
    },
    inviteUploadResumeHistoryId () {
      return this.$route.query.history_id
    },
    isFromPreference () {
      return this.$route.query.is_from_preference === '1'
    },
    cancelBtnTextI18nKey () {
      return this.isFromPreference ? 'common.btn.back' : 'common.btn.cancel'
    },
    useAIResumeParse () {
      return this.companyThemeSetting?.company.has_ai_resume_parse_premium
    }
  },
  created () {
    this.resumeInfo = {}
    const promiseList = [this.getResumeInfo(), this.loadJobApplyStatus(), this.loadPersonalInfo()]
    this.readyPromise = promiseList
    this.$eventBus.$emit('showLoading', true)
    Promise.all(this.readyPromise).then(() => {
      this.initResumeSetting()
      this.ready = true
    }).finally(() => {
      this.$eventBus.$emit('showLoading', false)
    })
  },
  mounted () {
    if (this.onlyUploadResume) {
      // 只上传简历，不显示其他页面
      this.resumeEditorSetting.skipParseResume = !this.parseResume
      this.resumeEditorSetting.displayStep.push(STEP_TYPE.UPLOAD_RESUME)
      this.resumeEditorSetting[STEP_TYPE.UPLOAD_RESUME].onlyUploadResume = true
      this.resumeEditorSetting.hiddenStep.splice(this.resumeEditorSetting.hiddenStep.indexOf(STEP_TYPE.UPLOAD_RESUME), 1)
      const hiddenStep = [STEP_TYPE.BASE_INFO, STEP_TYPE.DUPLICATE_CANDIDATE, STEP_TYPE.EDUCATION, STEP_TYPE.WORK_EXPERIENCE, STEP_TYPE.LANGUAGE, STEP_TYPE.ADDITIONAL_INFO]
      this.resumeEditorSetting.hiddenStep = _.concat(this.resumeEditorSetting.hiddenStep, hiddenStep)
    }
    if (this.onlyOpenQuestion) {
      // 只回答openQuestion，不显示其他页面
      this.resumeEditorSetting.onlyOpenQuestion = true
      this.resumeEditorSetting.displayStep.push(STEP_TYPE.JOB_QUESTIONNAIRE)
      const hiddenStep = [STEP_TYPE.UPLOAD_RESUME, STEP_TYPE.BASE_INFO, STEP_TYPE.DUPLICATE_CANDIDATE, STEP_TYPE.EDUCATION, STEP_TYPE.WORK_EXPERIENCE, STEP_TYPE.LANGUAGE, STEP_TYPE.ADDITIONAL_INFO]
      this.resumeEditorSetting.hiddenStep = _.concat(this.resumeEditorSetting.hiddenStep, hiddenStep)
    }
    if (this.onlyInviteQuestion) {
      // 只回答inviteQuestion，不显示其他页面
      this.resumeEditorSetting.onlyInviteQuestion = true
      this.resumeEditorSetting.displayStep.push(STEP_TYPE.INVITE_QUESTIONNAIRE)
      const hiddenStep = [STEP_TYPE.UPLOAD_RESUME, STEP_TYPE.BASE_INFO, STEP_TYPE.DUPLICATE_CANDIDATE, STEP_TYPE.EDUCATION, STEP_TYPE.WORK_EXPERIENCE, STEP_TYPE.LANGUAGE, STEP_TYPE.ADDITIONAL_INFO]
      this.resumeEditorSetting.hiddenStep = _.concat(this.resumeEditorSetting.hiddenStep, hiddenStep)
    }
    if (this.isFromPreference) {
      this.resumeEditorSetting[STEP_TYPE.UPLOAD_RESUME].showCreateNewBtn = true
      this.resumeEditorSetting[STEP_TYPE.UPLOAD_RESUME].immediateUpload = true
      this.resumeEditorSetting.showRightBtn = true
      this.resumeEditorSetting.rightBtnTextI18nKey = 'admin.application.candidate.btn.skip'
    }
  },
  methods: {
    ...mapActions('personal', [
      'getResumeData',
      'createOrUpdateResumeData',
      'uploadCandidatePhoto',
      'getMiniResumeData',
      'createOrUpdateMiniResumeData',
      'getBasicResumeData',
      'createOrUpdateBasicResumeData',
      'personalUploadResume',
      'updatePersonalCandidateAvatar',
      'getResumeApplySuccessInfo',
      'getPersonalInfo',
      'saveJobQuestion',
      'getJobQuestion',
      'getJobInviteQuestion',
      'saveJobInviteQuestion',
      'dispatchApply',
      'parseCandidateResumeByCandidate',
      'createParseResumeInfoByCandidateTask',
      'getParseResumeInfoByCandidateTaskStatus'
    ]),
    ...mapActions('job', ['getJobApplyStatus']),
    ...mapActions('application', ['getJobApplyRequiredInfo']),
    async getResumeInfo () {
      let getResumeMethod = this.getResumeData
      if (this.isMiniResume) {
        getResumeMethod = this.getMiniResumeData
      } else if (this.isBasicResume) {
        getResumeMethod = this.getBasicResumeData
      }

      const resp = await getResumeMethod({ job_id: this.jobId, company_id: this.companyId })
      if (resp) {
        this.resumeInfo = resp
      }
      if (this.jobId) {
        const jobApplyRequiredInfoResp = await this.getJobApplyRequiredInfo(this.jobId)
        if (jobApplyRequiredInfoResp) {
          await this.dealJobApplyRequiredInfo(jobApplyRequiredInfoResp)
        }
        if (!this.onlyEditResume && !this.onlyUploadResume) {
          await this.getJobOpenQuestion()
        }
      }
    },
    async handleCancel () {
      if (this.isFromPreference) {
        this.$router.back()
      } else {
        const back = this.$route.query.back || `/recruiting/personal/home?company_id=${this.companyId}`
        mixedRouterPush(back)
      }
    },
    handleRightBtnClick () {
      if (this.isFromPreference) {
        const backUrl = this.$route.query.back
        mixedRouterPush(backUrl)
      }
    },
    handleToHome (homePage) {
      this.$router.push({ path: '/recruiting/personal/home', query: { company_id: this.companyId } })
    },
    async handleUploadResume (resumeData) {
      this.useThirdPartyUpload = true
      await this.handleSave(resumeData, true)
    },
    async handelSaveOpenQuestion (resumeData) {
      this.$eventBus.$emit('showLoading', true)
      try {
        await this.handleSaveOpenQuestion(resumeData.open_question)
        await this.goBack(true, true)
      } finally {
        this.$eventBus.$emit('showLoading', false)
      }
    },
    async handleSave (resumeData, autoBack, isLastStep) {
      let resumeParams = resumeData
      let uploadResumeRef = null
      if (this.$refs.editor && this.$refs.editor.$refs[`stepComponent_${STEP_TYPE.UPLOAD_RESUME}`]) {
        uploadResumeRef = this.$refs.editor.$refs[`stepComponent_${STEP_TYPE.UPLOAD_RESUME}`][0]
      }
      if (this.onlyUploadResume && uploadResumeRef) {
        if (!this.useThirdPartyUpload) {
          const file = uploadResumeRef.currentFile
          if (!file) {
            this.$eventBus.$emit('showLoading', false)
            return
          }
          const fileExt = getFileExtension(file)
          const fileKey = generateTempFileKey(fileExt)
          await uploadFile(file, fileKey)

          if (this.resumeEditorSetting.skipParseResume) {
            this.resumeInfo.resumePath = fileKey
          } else {
            if (this.useAIResumeParse) {
              this.$eventBus.$emit('showAIResumeParseDialog', true)
            } else {
              this.$eventBus.$emit('showLoading', true)
            }
            try {
              const resp = await this.parseResumeFile(fileKey)
              this.$refs.editor.resetCandidateInfo(resp, true)
              this.resumeInfo = this.$refs.editor.candidateInfo
            } finally {
              if (this.useAIResumeParse) {
                this.$eventBus.$emit('showAIResumeParseDialog', false)
              } else {
                this.$eventBus.$emit('showLoading', false)
              }
            }
          }
          resumeParams = this.resumeInfo
        }
        this.resumeInfo.isUpload = true
        resumeParams.only_upload_resume = true
        resumeParams.history_id = this.inviteUploadResumeHistoryId
      }
      this.$eventBus.$emit('showLoading', true)
      try {
        let saveMethod = this.createOrUpdateResumeData
        if (this.isMiniResume) {
          saveMethod = this.createOrUpdateMiniResumeData
        } else if (this.isBasicResume) {
          saveMethod = this.createOrUpdateBasicResumeData
        }
        resumeParams.update_resume_step = true
        resumeParams.is_from_preference = this.isFromPreference
        await saveMethod({ jobId: this.jobId, companyId: this.companyId, updateResumeStep: true, resumeData: resumeParams })
        if (this.isFromPreference && this.onlyUploadResume) {
          this.$message.success(this.$t('admin.application.candidate.message.uploadSuccess'))
        }
        const photo = resumeParams.baseInfo?.photoFile
        if (photo) {
          await this.uploadCandidatePhoto({ photo: photo, jobId: this.jobId, companyId: this.companyId })
        }
        if (autoBack) {
          if (this.hasOpenQuestion) {
            await this.goBack(false, isLastStep)
          } else {
            await this.goBack(true, isLastStep)
          }
        }
      } finally {
        this.$eventBus.$emit('showLoading', false)
      }
    },
    async goBack (afterOpenQuestion, isLastStep) {
      if (this.isFromPreference) {
        const back = this.$route.query.back
        if (back) {
          mixedRouterPush(back)
        }
        return
      }
      if (this.onlyUploadResume) {
        this.uploadResumeSuccessDialogShow = true
        return
      }
      const applyUrl = this.applyUrl || ''
      const jobId = this.jobId

      if (applyUrl) {
        const params = this.getParams(applyUrl)
        if (params.assessment) {
          this.assessment = true
        }
        params.need_response_json = 1
        if (afterOpenQuestion) {
          params.after_open_question = 1
        }
        if (this.needUploadResume) {
          params.uploaded_resume = 1
        }
        if (isLastStep) {
          this.applying = true
          this.resumeApplySuccessDialogShow = true
          this.$eventBus.$emit('showLoading', false)
        }
        const resp = await this.dispatchApply({ jobId: jobId, params: params })
        const respData = resp.data
        if (respData && respData.need_assessment) {
          mixedRouterPush(`${applyUrl}${applyUrl.includes('?') ? '&after_open_question=1' : '?after_open_question=1'}${this.needUploadResume ? '?uploaded_resume=1' : ''}`)
          return
        }
        if (respData && respData.apply === 'success') {
          const params = this.getParams(respData.params)
          const resumeData = await this.getResumeApplySuccessInfo({ jobId: this.jobId, params: params })
          this.resumeApplyData = resumeData
          this.resumeApplySuccessDialogShow = true
          this.applying = false
        }
      } else if (jobId) {
        const resp = await this.updatePersonalCandidateAvatar({ jobId: jobId, data: { companyId: this.companyId } })
        if (resp) {
          this.applying = true
          this.resumeApplySuccessDialogShow = true
          this.$eventBus.$emit('showLoading', false)
          const resp = await this.dispatchApply({ jobId: jobId, params: { need_response_json: 1 } })
          const respData = resp.data
          if (respData && respData.apply === 'success') {
            const params = this.getParams(respData.params)
            const resumeData = await this.getResumeApplySuccessInfo({ jobId: this.jobId, params: params })
            this.resumeApplyData = resumeData
            this.resumeApplySuccessDialogShow = true
            this.applying = false
          } else {
            mixedRouterPush(`/apply-job-process/${jobId}/`)
          }
        }
      } else {
        this.$router.push({ path: '/recruiting/personal/profile', query: { company_id: this.companyId } })
      }
    },
    getParams (url) {
      const params = {}
      const paramStr = url.split('?')[1] // 获取 URL 中的查询参数部分

      if (!paramStr) {
        return params
      }

      const paramPairs = paramStr.split('&') // 分割参数对

      paramPairs.forEach(pair => {
        const [key, value] = pair.split('=') // 分割键和值
        params[decodeURIComponent(key)] = decodeURIComponent(value || '') // 存储到对象中，解码 URL 编码
      })

      return params
    },
    handleSaveAndBack (jobTemplateInfo) {
      this.handleSave(jobTemplateInfo, true, true)
    },
    async loadPersonalInfo () {
      const resp = await this.getPersonalInfo({ company_id: this.companyId })
      if (resp) {
        if (resp.need_create_password) {
          return this.$router.push({ path: `/passport/candidate/create-password/${resp.password_reset_code}/`, query: { back: this.$route.fullPath } })
        }
        this.personalInfo = resp
        this.resumeEditorSetting.profileStep = this.personalInfo.profile_step
      }
    },
    async loadJobApplyStatus () {
      if (this.jobId) {
        const resp = await this.getJobApplyStatus({ job_id: this.jobId })
        if (resp) {
          this.canReapply = resp.can_reapply
          this.finishedApply = resp.finish_apply === 1
        }
      }
    },
    async dealJobApplyRequiredInfo (info) {
      if (!info) {
        return
      }
      const requiredField = info.required_field || []
      if (this.onlyOpenQuestion || this.onlyInviteQuestion) {
        this.resumeEditorSetting[STEP_TYPE.BASE_INFO].showUploadButton = false
        this.resumeEditorSetting.hiddenStep.push(STEP_TYPE.UPLOAD_RESUME)
        return
      }
      // 重复申请 或者 求职者申请成功后重新返回申请页面，也需要上传简历
      if (this.resumeInfo.is_application_resume && !this.canReapply && !this.finishedApply) {
        if (this.resumeInfo.baseInfo.has_upload_resume) {
          this.resumeEditorSetting[STEP_TYPE.BASE_INFO].showUploadButton = false
          this.resumeEditorSetting.hiddenStep.push(STEP_TYPE.UPLOAD_RESUME)
          return
        } else {
          this.resumeEditorSetting.skipParseResume = true
        }
      }
      if (requiredField.includes('resume')) {
        this.needUploadResume = true
        this.resumeEditorSetting[STEP_TYPE.UPLOAD_RESUME].needUploadResume = true
        this.resumeEditorSetting[STEP_TYPE.BASE_INFO].showUploadButton = false
        this.resumeEditorSetting[STEP_TYPE.BASE_INFO].loadCandidateInfoWhenCreated = true
        this.resumeEditorSetting.displayStep.push(STEP_TYPE.UPLOAD_RESUME)
        if (this.resumeEditorSetting.hiddenStep.indexOf(STEP_TYPE.UPLOAD_RESUME) > 0) {
          this.resumeEditorSetting.hiddenStep.splice(this.resumeEditorSetting.hiddenStep.indexOf(STEP_TYPE.UPLOAD_RESUME), 1)
        }
      }
      if (info.hidden_field) {
        this.resumeEditorSetting[STEP_TYPE.BASE_INFO].hiddenField.push(...info.hidden_field)
      }
      if (info.work_experience_count) {
        this.resumeEditorSetting[STEP_TYPE.WORK_EXPERIENCE].workCount = info.work_experience_count
      }
      if (info.has_open_question_for_employee) {
        this.resumeEditorSetting.displayStep.splice(0)
        const hiddenStep = [STEP_TYPE.UPLOAD_RESUME, STEP_TYPE.BASE_INFO, STEP_TYPE.DUPLICATE_CANDIDATE, STEP_TYPE.EDUCATION, STEP_TYPE.WORK_EXPERIENCE, STEP_TYPE.LANGUAGE, STEP_TYPE.ADDITIONAL_INFO]
        this.resumeEditorSetting.hiddenStep = _.concat(this.resumeEditorSetting.hiddenStep, hiddenStep)
      }
    },
    async getJobOpenQuestion () {
      const openQuestion = {
        standard: [],
        invite: [],
        showStandardQrImage: null,
        showInviteQrImage: null
      }
      if (!this.onlyInviteQuestion) {
        const resp = await this.getJobQuestion({ jobId: this.jobId })
        if (resp?.questions && !_.isEmpty(resp.questions)) {
          this.hasOpenQuestion = true
          this.resumeEditorSetting.hiddenStep.splice(this.resumeEditorSetting.hiddenStep.indexOf(STEP_TYPE.JOB_QUESTIONNAIRE), 1)
          openQuestion.standard = this.formatOpenQuestion(resp.questions)
          openQuestion.showStandardQrImage = resp.show_qr_image ? 'standard' : null
        }
      }
      const resp = await this.getJobInviteQuestion({ jobId: this.jobId })
      if (resp?.questions && !_.isEmpty(resp.questions)) {
        this.hasOpenQuestion = true
        this.resumeEditorSetting.hiddenStep.splice(this.resumeEditorSetting.hiddenStep.indexOf(STEP_TYPE.INVITE_QUESTIONNAIRE), 1)
        openQuestion.invite = this.formatOpenQuestion(resp.questions)
        openQuestion.showInviteQrImage = resp.show_qr_image ? 'invite' : null
      }
      this.$set(this.resumeInfo, 'open_question', openQuestion)
    },
    async handleSaveOpenQuestion (openQuestion) {
      let standardParams = null
      let inviteParams = null
      if (!openQuestion.standardQrImage) {
        standardParams = this.formatOpenQuestionAnswer(openQuestion?.standard)
      }
      if (!openQuestion.inviteQrImage) {
        inviteParams = this.formatOpenQuestionAnswer(openQuestion?.invite)
      }
      if (standardParams && !_.isEmpty(standardParams)) {
        await this.saveJobQuestion({ jobId: this.jobId, data: standardParams })
      }
      if (inviteParams && !_.isEmpty(inviteParams)) {
        await this.saveJobInviteQuestion({ jobId: this.jobId, data: inviteParams })
      }
    },
    formatOpenQuestion (questions) {
      return _.map(questions, question => {
        const result = question.result
        return {
          duration: question.duration,
          type: question.type,
          title: question.title,
          id: question.id,
          options: question.option,
          answer_value: result?.value,
          answer_file_key: result?.video_resource_key,
          required: true
        }
      })
    },
    formatOpenQuestionAnswer (question) {
      if (!_.isEmpty(question) && this.jobId) {
        const answer = {}
        _.forEach(question, item => {
          if ([QUESTION_TYPE.VIDEO, QUESTION_TYPE.ATTACHMENT].includes(item.type)) {
            answer[item.id] = {
              videoKey: item.answer_file_key,
              description: item.answer_value || ''
            }
          } else {
            answer[item.id] = item.answer_value
          }
        })
        if (_.isEmpty(answer)) {
          return answer
        }
        const params = {
          is_last: 1,
          answer: answer
        }
        return params
      }
    },
    initResumeSetting () {
      const baseInfoSetting = this.resumeEditorSetting[STEP_TYPE.BASE_INFO]
      if (this.isMiniResume || this.isBasicResume) {
        this.resumeEditorSetting.hiddenStep.push(...this.miniResumeHiddenStep)
        baseInfoSetting.requiredField.push('address')
        if (this.isBasicResume) {
          this.resumeEditorSetting.hiddenStep.push(STEP_TYPE.WORK_EXPERIENCE)
          this.resumeEditorSetting.hiddenStep.push(STEP_TYPE.EDUCATION)
          this.resumeEditorSetting[STEP_TYPE.BASE_INFO].showUploadButton = false
        }
        // if (!this.jobId && this.resumeEditorSetting[STEP_TYPE.WORK_EXPERIENCE].workCount <= 1) {
        //   this.resumeEditorSetting[STEP_TYPE.WORK_EXPERIENCE].workCount = 1
        // }
      }
    },
    handelImportResume () {
      const displayStep = this.resumeEditorSetting.displayStep
      const hiddenStep = this.resumeEditorSetting.hiddenStep
      if (displayStep.indexOf(STEP_TYPE.UPLOAD_RESUME) === -1) {
        displayStep.push(STEP_TYPE.UPLOAD_RESUME)
      }
      if (hiddenStep.indexOf(STEP_TYPE.UPLOAD_RESUME) !== -1) {
        hiddenStep.splice(this.resumeEditorSetting.hiddenStep.indexOf(STEP_TYPE.UPLOAD_RESUME), 1)
      }
    },
    handleViewReport () {
      window.open(this.$DjangoBaseUrl(`/report/candidate/?job_id=${this.jobId ? this.jobId : 0}`), '_blank')
    },
    handleContinueApply () {
      mixedRouterPush(`/recruiting/company/${this.resumeApplyData.company?.id}/job-list`)
    },
    handleChangeResumeSetting () {
      this.$router.replace({
        query: {
          ...this.$route.query,
          only_upload_resume: 0
        }
      })
      const showStep = [
        STEP_TYPE.UPLOAD_RESUME,
        STEP_TYPE.BASE_INFO,
        STEP_TYPE.EDUCATION,
        STEP_TYPE.WORK_EXPERIENCE,
        STEP_TYPE.LANGUAGE
      ]
      this.resumeEditorSetting.hiddenStep = _.filter(this.resumeEditorSetting.hiddenStep, step => !_.includes(showStep, step))
      this.resumeEditorSetting[STEP_TYPE.UPLOAD_RESUME].onlyUploadResume = false
      this.resumeEditorSetting[STEP_TYPE.UPLOAD_RESUME].immediateUpload = false
      this.resumeEditorSetting.skipParseResume = false
      this.resumeEditorSetting.showRightBtn = false
    },
    async parseResumeFile (fileKey) {
      const resp = await this.createParseResumeInfoByCandidateTask({ resume_file: fileKey, job_id: this.jobId, company_id: this.companyId })
      if (resp) {
        const taskId = resp.task_id
        this.parseCount = 1
        const resumeInfo = await this.getParseResumeInfo(taskId)
        return resumeInfo
      }
    },
    async getParseResumeInfo (taskId) {
      if (this.parseCount > AI_PARSE_MAX_COUNT) {
        this.$message.error(this.$t('admin.application.candidate.message.aiParseResumeTimeout'))
        return
      }
      const resp = await this.getParseResumeInfoByCandidateTaskStatus({ task_id: taskId })
      if (resp) {
        const status = resp.status
        if (status === 'PENDING') {
          return new Promise((resolve) => {
            setTimeout(async () => {
              const result = await this.getParseResumeInfo(taskId)
              resolve(result)
            }, 1000)
          })
        } else if (status === 'SUCCESS') {
          return resp.result
        } else {
          if (this.useAIResumeParse) {
            this.$message.error(this.$t('admin.application.candidate.message.aiParseResumeError'))
          } else {
            this.$message.error(this.$t('admin.application.candidate.message.parseResumeError'))
          }
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.resume-editor-container {
  .resume-editor {
    min-width: 1325px;
    overflow: auto;
  }

  .upload-success-dialog {
    .upload-success-container {
      display: flex;
      width: 100%;
      flex-direction: column;
      align-items: center;

      .result-icon {
        font-size: 50px;
        color: #52C41A;
      }

      .title-label {
        margin-top: 32px;
        font-size: 22px;
        font-weight: 500;
        color: #303133;
        line-height: 30px;
        word-break: break-word;
        white-space: normal;
      }
      .title-tips {
        margin-top: 50px;
        height: 22px;
        font-size: 16px;
        font-weight: 400;
        color: #606266;
        line-height: 22px;
      }

      .button {
        width: 260px;
        height: 40px;
        margin: 0;
        border-radius: 2px;
      }

      .to-personal-home {
        margin-top: 50px;
      }
    }
  }

  .apply-success-dialog {
    text-align: center;

    .domain {
      position: absolute;
      right: 25px;
      top: 30px;

      .companyLogo {
        height: 40px;
      }
    }

    .success-icon {
      font-size: 50px;
      color: #52C41A;
    }

    .title-label {
      font-weight: 500;
      font-size: 22px;
      color: #303133;
      line-height: 30px;
      margin: 15px 0;
    }

    .title-description {
      font-weight: 400;
      font-size: 16px;
      color: #606266;
      line-height: 22px;
      margin: 5px 0;
    }

    .coach-ai-interview-qrcode {
      margin-top: 30px;
      font-size: 14px;
      color: #666;
      text-align: center;

      .qrcode {
        margin-top: 10px;
        width: 150px;
        height: 150px;
      }
    }

    .btn-group {
      margin: 20px 0;
      display: flex;
      align-items: center;
      flex-direction: column;

      .button {
        width: 260px;
        height: 40px;
        margin: 5px 0;
      }
    }
  }
}
</style>
